import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  UNIT_STATUSES, ROOM_TYPES, UNIT_TYPES, ASPECTS, VIEWS, FLOOR_POSITIONS, CURRENCIES,
  formatPrice,
} from "@/lib/constants";
import { Link } from "react-router-dom";
import { Filter, X, FileUp, Edit3 } from "lucide-react";
import { toast } from "sonner";

export default function Units() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [units, setUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [floors, setFloors] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    project_id: searchParams.get("project_id") || "",
    block_id: "",
    floor_id: "",
    unit_type: "",
    room_type: "",
    layout_type: "",
    aspect: "",
    view: "",
    floor_position: "",
    status: searchParams.get("status") || "",
    sales_agent_id: "",
    currency: "",
    min_price: "",
    max_price: "",
    min_area: "",
    max_area: "",
    has_interest: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const urlProjectId = searchParams.get("project_id") || "";
  const urlStatus = searchParams.get("status") || "";

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) params[k] = v; });
    try {
      const { data } = await api.get("/units", { params });
      setUnits(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data));
    api.get("/company/users").then(({ data }) => setUsers(data));
  }, []);

  useEffect(() => {
    setFilters((f) => f.project_id === urlProjectId && f.status === urlStatus ? f : ({
      ...f, project_id: urlProjectId, status: urlStatus,
    }));
  }, [urlProjectId, urlStatus]);

  useEffect(() => {
    if (filters.project_id) {
      api.get(`/projects/${filters.project_id}/blocks`).then(({ data }) => setBlocks(data));
    } else {
      setBlocks([]); setFloors([]);
    }
  }, [filters.project_id]);

  useEffect(() => {
    if (filters.block_id) {
      api.get(`/blocks/${filters.block_id}/floors`).then(({ data }) => setFloors(data));
    } else {
      setFloors([]);
    }
  }, [filters.block_id]);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      ...(key === "project_id" ? { block_id: "", floor_id: "" } : {}),
      ...(key === "block_id" ? { floor_id: "" } : {}),
    }));
    if (key === "project_id" || key === "status") {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      }, { replace: true });
    }
  };
  const clear = () => {
    setFilters({
      project_id: "", block_id: "", floor_id: "", unit_type: "", room_type: "",
      layout_type: "", aspect: "", view: "", floor_position: "", status: "", sales_agent_id: "",
      currency: "", min_price: "", max_price: "", min_area: "", max_area: "", has_interest: "",
    });
    setSearchParams({}, { replace: true });
  };
  const filterLabels = {
    project_id: "Proje", block_id: "Blok", floor_id: "Kat", status: "Durum",
    unit_type: "Bölüm Tipi", room_type: "Oda Tipi", layout_type: "Plan Tipi",
    aspect: "Cephe", view: "Manzara", floor_position: "Kat Konumu",
    sales_agent_id: "Personel", currency: "Para Birimi", min_price: "Min Fiyat",
    max_price: "Max Fiyat", min_area: "Min m²", max_area: "Max m²",
    has_interest: "İlgilenen Müşteri",
  };
  const filterValue = (key, value) => {
    if (key === "project_id") return projects.find((p) => p.id === value)?.name || value;
    if (key === "block_id") return blocks.find((b) => b.id === value)?.name || value;
    if (key === "floor_id") return floors.find((f) => f.id === value)?.name || value;
    if (key === "sales_agent_id") return users.find((u) => u.id === value)?.name || value;
    if (key === "has_interest") return value === "true" ? "Var" : "Yok";
    return value;
  };
  const activeFilters = Object.entries(filters).filter(([, value]) => value !== "" && value != null);

  return (
    <div className="space-y-6" data-testid="units-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-display">Stok Listesi</h1>
          <p className="text-slate-500 mt-1">Tüm projelerdeki daire, ofis ve dükkânları filtreleyin.</p>
          <p className="text-sm text-slate-500 mt-1">{units.length} sonuç</p>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <button onClick={() => setShowFilters((s) => !s)}
                  className="bg-white border border-slate-200 hover:border-slate-300 px-3 py-2 text-sm rounded-lg inline-flex items-center gap-2"
                  data-testid="toggle-filters">
            <Filter className="w-4 h-4" /> Filtreler{activeFilters.length > 0 ? ` (${activeFilters.length})` : ""}
          </button>
          {user?.role !== "agent" && (
            <>
              <Link to="/import" className="bg-white border border-slate-200 hover:border-slate-300 px-3 py-2 text-sm rounded-lg inline-flex items-center gap-2" data-testid="import-link">
                <FileUp className="w-4 h-4" /> Excel/CSV Yükle
              </Link>
              {user?.role === "admin" && (
                <Link to="/bulk-price" className="bg-white border border-slate-200 hover:border-slate-300 px-3 py-2 text-sm rounded-lg inline-flex items-center gap-2" data-testid="bulk-price-link">
                  <Edit3 className="w-4 h-4" /> Toplu Fiyat
                </Link>
              )}
            </>
          )}
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="active-unit-filters">
          {activeFilters.map(([key, value]) => (
            <button key={key} type="button" onClick={() => update(key, "")}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-slate-700 bg-white border border-slate-200 rounded hover:border-slate-400"
                    aria-label={`${filterLabels[key]} filtresini kaldır`}>
              <span className="font-semibold">{filterLabels[key]}:</span> {filterValue(key, value)}
              <X className="w-3 h-3" />
            </button>
          ))}
          <button type="button" onClick={clear} className="text-xs text-slate-500 hover:text-slate-900">Tümünü temizle</button>
        </div>
      )}

      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="filters-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Select label="Proje" value={filters.project_id} onChange={(v) => update("project_id", v)}
                    options={[["", "Tümü"], ...projects.map((p) => [p.id, p.name])]} testid="filter-project" />
            <Select label="Blok" value={filters.block_id} onChange={(v) => update("block_id", v)}
                    options={[["", "Tümü"], ...blocks.map((b) => [b.id, b.name])]} testid="filter-block" />
            <Select label="Kat" value={filters.floor_id} onChange={(v) => update("floor_id", v)}
                    options={[["", "Tümü"], ...floors.map((f) => [f.id, f.name])]} testid="filter-floor" />
            <Select label="Durum" value={filters.status} onChange={(v) => update("status", v)}
                    options={[["", "Tümü"], ...UNIT_STATUSES.map((s) => [s, s])]} testid="filter-status" />
            <Select label="Bölüm Tipi" value={filters.unit_type} onChange={(v) => update("unit_type", v)}
                    options={[["", "Tümü"], ...UNIT_TYPES.map((s) => [s, s])]} testid="filter-unit-type" />
            <Select label="Oda Tipi" value={filters.room_type} onChange={(v) => update("room_type", v)}
                    options={[["", "Tümü"], ...ROOM_TYPES.map((s) => [s, s])]} testid="filter-room-type" />
            <Inp label="Plan Tipi" value={filters.layout_type} onChange={(v) => update("layout_type", v)} />
            <Select label="Cephe" value={filters.aspect} onChange={(v) => update("aspect", v)}
                    options={[["", "Tümü"], ...ASPECTS.map((s) => [s, s])]} testid="filter-aspect" />
            <Select label="Manzara" value={filters.view} onChange={(v) => update("view", v)}
                    options={[["", "Tümü"], ...VIEWS.map((s) => [s, s])]} testid="filter-view" />
            <Select label="Kat Konumu" value={filters.floor_position} onChange={(v) => update("floor_position", v)}
                    options={[["", "Tümü"], ...FLOOR_POSITIONS.map((s) => [s, s])]} />
            <Select label="Para Birimi" value={filters.currency} onChange={(v) => update("currency", v)}
                    options={[["", "Tümü"], ...CURRENCIES.map((s) => [s, s])]} />
            <Select label="Sorumlu Personel" value={filters.sales_agent_id} onChange={(v) => update("sales_agent_id", v)}
                    options={[["", "Tümü"], ...users.map((u) => [u.id, u.name])]} />
            <Select label="İlgilenen Müşteri" value={filters.has_interest} onChange={(v) => update("has_interest", v)}
                    options={[["", "Tümü"], ["true", "Var"], ["false", "Yok"]]} />
            <Inp label="Min Fiyat" value={filters.min_price} onChange={(v) => update("min_price", v)} type="number" />
            <Inp label="Max Fiyat" value={filters.max_price} onChange={(v) => update("max_price", v)} type="number" />
            <Inp label="Min m²" value={filters.min_area} onChange={(v) => update("min_area", v)} type="number" />
            <Inp label="Max m²" value={filters.max_area} onChange={(v) => update("max_area", v)} type="number" />
          </div>
          <div className="flex justify-end">
            <button onClick={clear} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <X className="w-3 h-3" /> Filtreleri Temizle
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Proje</th>
              <th className="text-left px-4 py-3">Blok / Kat</th>
              <th className="text-left px-4 py-3">Daire</th>
              <th className="text-left px-4 py-3">Tip</th>
              <th className="text-left px-4 py-3">Oda</th>
              <th className="text-left px-4 py-3">Plan Tipi</th>
              <th className="text-left px-4 py-3">m² (Net/Brüt)</th>
              <th className="text-left px-4 py-3">Cephe</th>
              <th className="text-left px-4 py-3">Manzara</th>
              <th className="text-left px-4 py-3">Personel</th>
              <th className="text-left px-4 py-3">Fiyat</th>
              <th className="text-left px-4 py-3">Durum</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="px-4 py-12 text-center text-slate-500">Yükleniyor…</td></tr>
            ) : units.length === 0 ? (
              <tr><td colSpan={12} className="px-4 py-12 text-center text-slate-400">Sonuç yok</td></tr>
            ) : units.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => nav(`/units/${u.id}`)} data-testid={`unit-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{u.project_name || "Proje bilgisi yok"}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="font-medium text-slate-700">{u.block_name || "Blok bilgisi yok"}</div>
                  <div className="text-slate-500">{u.floor_name || "Kat bilgisi yok"}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{u.unit_no}</div>
                </td>
                <td className="px-4 py-3">{u.unit_type}</td>
                <td className="px-4 py-3">{u.room_type}</td>
                <td className="px-4 py-3">{u.layout_type || "—"}</td>
                <td className="px-4 py-3">{u.net_area}/{u.gross_area}</td>
                <td className="px-4 py-3">{u.aspect}</td>
                <td className="px-4 py-3">{u.view}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{u.sales_agent_name || "—"}</td>
                <td className="px-4 py-3 font-semibold">{formatPrice(u.price, u.currency)}</td>
                <td className="px-4 py-3"><span className={`status-chip ${"status-" + (u.status === "Kapora Alındı" ? "Kapora" : u.status?.replace(/\s+/g, ""))}`}><span className="status-dot" />{u.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input" data-testid={testid}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
function Inp({ label, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="input" />
    </label>
  );
}
